import { PromotedServiceAssignment, PromotedServiceAssignmentResult } from '@mews-commander/data-access';
import { createZodFormResolver, lt, showErrorAlert, useFormControls } from '@mews/framework';
import { Button, Stack } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { DataTestId, TextKey } from '@mews-commander/enums';
import { FormValues, servicePromotionSchema } from '../schema';
import { useUpdateServicePromotionMutation } from '../queries/useUpdateServicePromotionMutation';
import { useAddServicePromotionMutation } from '../queries';
import { getIsExistingService } from '../utils/queryUtils';
import { useGetInitialFormValues } from './form/hooks/useGetInitialFormValues';
import { ServicePromotionForm } from './form/ServicePromotionForm';

interface ServicePromotionDialogProps {
    active: boolean;
    onClose: () => void;
    isEditMode?: boolean;
    enterpriseId: string;
    serviceId: string;
    promotedService?: PromotedServiceAssignment;
    promotedServices: PromotedServiceAssignment[];
    onSuccess: (data: PromotedServiceAssignment) => void;
}

export const ServicePromotionDialog = ({
    isEditMode = false,
    enterpriseId,
    active,
    onClose,
    serviceId,
    promotedService,
    promotedServices,
    onSuccess,
}: ServicePromotionDialogProps) => {
    const defaultValues = useGetInitialFormValues(isEditMode, promotedService);

    const mutationFn = isEditMode ? useUpdateServicePromotionMutation : useAddServicePromotionMutation;

    const promotedServiceAssignmentId = promotedService?.id;

    const onSuccessAction = (data: PromotedServiceAssignmentResult) => {
        const createdOrUpdatedService = data.promotedServiceAssignments[0];
        onSuccess(createdOrUpdatedService);
    };

    const { mutate } = mutationFn({
        serviceId,
        ...(isEditMode && { promotedServiceAssignmentId }),
        onSuccess: onSuccessAction,
    });

    const onSubmit = (formValues: FormValues) => {
        const { description, ...requiredFields } = formValues;
        const isExistingService = getIsExistingService({
            existingServices: promotedServices,
            newService: formValues,
            editedPromotionId: promotedServiceAssignmentId,
        });
        if (isExistingService) {
            showErrorAlert({
                title: isEditMode
                    ? lt(TextKey.PromotedServiceAssignmentUpdateErrorServiceAlreadyExistsTitle)
                    : lt(TextKey.PromotedServiceAssignmentAddErrorServiceAlreadyExistsTitle),
                description: lt(
                    isEditMode
                        ? TextKey.PromotedServiceAssignmentUpdateErrorServiceAlreadyExists
                        : TextKey.PromotedServiceAssignmentAddErrorServiceAlreadyExists
                ),
            });
            return;
        }
        mutate(requiredFields, { onSuccess: handleClose });
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const { renderFormContent, reset, isValid, submit, hasDirtyFields } = useFormControls<FormValues>({
        defaultValues,
        onSubmit,
        resolver: createZodFormResolver(servicePromotionSchema),
        mode: 'onChange',
    });

    const isSubmitDisabled = !isValid || (isEditMode && !hasDirtyFields);

    const footer = {
        primary: (
            <Button
                data-test-id={DataTestId.ServicePromotionsSavePromotionButton}
                variant="primary"
                type="submit"
                size="medium"
                disabled={isSubmitDisabled}
                onClick={submit}
            >
                {lt(isEditMode ? TextKey.Save : TextKey.Create)}
            </Button>
        ),
        secondary: (
            <Button variant="tertiary" type="button" size="medium" onClick={handleClose}>
                {lt(TextKey.Cancel)}
            </Button>
        ),
    };

    return (
        <FloatingPanel
            active={active}
            onClose={handleClose}
            title={lt(
                isEditMode ? TextKey.PromotedServiceAssignmentDialogEdit : TextKey.PromotedServiceAssignmentDialogNew
            )}
            footer={footer}
        >
            <Stack distribution="stretch" fill>
                {renderFormContent(
                    <ServicePromotionForm
                        enterpriseId={enterpriseId}
                        serviceId={serviceId}
                        isEditMode={isEditMode}
                        promotedService={promotedService}
                    />
                )}
            </Stack>
        </FloatingPanel>
    );
};
